import { findSimilarPatientsFromInOfficeFile } from "app/services/manualUpload/manualUpload";
import {
  setInOfficeFile,
  setInOfficeFileName,
  setInOfficeUploadPatientInformationForManualUpload,
  setShowInOfficeUploadPatientFoundDetails,
  setSimilarPatientsFromInOfficeFile,
  setTransmissionType,
} from "app/store/manualUpload/slice/manualUploadSlice";
import { getFileExtension } from "app/utils/file";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAccountByAlternativeNames,
  validInOfficeFileExtensions,
} from "static/constants";
import { showErrorToast } from "utils/toast";

const useInOfficeFileUpload = ({ handlePatientSelect = () => {} }) => {
  const { inOfficeFile, inOfficeFileName } = useSelector(
    (state) => state.manualUpload
  );

  const { inOfficeUploadPatientDetails } = useSelector(
    (state) => state.manualUpload
  );

  const { accounts } = useSelector((state) => state.account);

  const {
    inOfficeData: {
      patientDeviceInformation = {
        hasAccessToAccountInNoteFile: false,
        clinic: "",
      },
    } = {},
  } = inOfficeUploadPatientDetails || {};

  const { hasAccessToAccountInNoteFile, clinic } = patientDeviceInformation;

  const [isProceedClicked, setIsProceedClicked] = useState(false);
  const [updatedAccountName, setUpdatedAccountName] = useState("");
  const [invalidAccountName, setInvalidAccountName] = useState(clinic);
  const [isSearchingSimilarPatients, setIsSearchingSimilarPatients] =
    useState(false);

  useEffect(() => {
    setInvalidAccountName(clinic || null);
  }, [hasAccessToAccountInNoteFile]);

  const dispatch = useDispatch();

  const handleFileUpload = (file) => {
    const uploadedFile = file[0];

    if (!uploadedFile) {
      return showErrorToast("No file was uploaded.");
    }

    const { name: fileName } = uploadedFile;
    const fileExtension = getFileExtension(fileName);

    if (!validInOfficeFileExtensions.includes(fileExtension)) {
      return showErrorToast("Invalid file format.");
    }

    dispatch(setInOfficeFile(uploadedFile));
    dispatch(setInOfficeFileName(fileName));
  };

  const cancelFile = () => {
    dispatch(setInOfficeFile(null));
    dispatch(setInOfficeFileName(""));
  };

  const handleInOfficePatientSelect = async () => {
    const inOfficeData = inOfficeUploadPatientDetails?.inOfficeData || {};
    const inOfficePatient = inOfficeUploadPatientDetails?.patient || null;
    const {
      isExistingPatient,
      patientDeviceInformation: {
        doi,
        dob,
        clinic,
        lastName,
        modelName,
        firstName,
        serialNumber,
        encounterDate,
      },
      patientDeviceAnalytics: {
        burden,
        bradySettings,
        leadInformation,
        deviceBatteryInformation,
      },
    } = inOfficeData;
    const payload = {
      leadInformation,
      deviceBatteryInformation,
      report_date: encounterDate,
      otherDeviceInformation: { ...burden, ...bradySettings },
    };

    const patientInformation = {
      ...(inOfficePatient || inOfficeData?.patientDeviceInformation || {}),
      ...payload,
      doi,
      physicians: [],
      device: modelName,
      modelNumber: serialNumber,
      account: getAccountByAlternativeNames(clinic, accounts),
    };

    dispatch(
      setInOfficeUploadPatientInformationForManualUpload(patientInformation)
    );
    dispatch(setTransmissionType("inOffice"));

    if (isExistingPatient) {
      return handlePatientSelection(patientInformation, isExistingPatient);
    }

    await handleNonExistingPatientSelect({ firstName, lastName, dob });
  };

  const handleNonExistingPatientSelect = async (patientInformation) => {
    try {
      setIsSearchingSimilarPatients(true);
      const response =
        await findSimilarPatientsFromInOfficeFile(patientInformation);

      if (response?.data?.length > 0) {
        return dispatch(setSimilarPatientsFromInOfficeFile(response.data));
      }
      return handlePatientSelection(patientInformation, false);
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsSearchingSimilarPatients(false);
    }
  };

  const handlePatientSelection = (patientInformation, isExistingPatient) => {
    handlePatientSelect(patientInformation, isExistingPatient);
    dispatch(setShowInOfficeUploadPatientFoundDetails(false));
    dispatch(setTransmissionType("inOffice"));
    cancelFile();
  };

  return {
    inOfficeFile,
    inOfficeFileName,
    isProceedClicked,
    updatedAccountName,
    invalidAccountName,
    hasAccessToAccountInNoteFile,
    patientDeviceInformation,
    inOfficeUploadPatientDetails,
    isSearchingSimilarPatients,

    cancelFile,
    handleFileUpload,
    setIsProceedClicked,
    setUpdatedAccountName,
    setInvalidAccountName,
    handleInOfficePatientSelect,
  };
};

export default useInOfficeFileUpload;
